export interface InterpretationSnapshot {
  id: string;
  target_type: string;
  target_key: string;
  lang: string | null;
  body: string;
  axes: string[];
  model_name: string | null;
  prompt_version: string | null;
  created_at: string | null;
}

export interface InterpretationReview {
  id: string;
  user_id: string;
  snapshot_id: string;
  target_type: string;
  target_key: string;
  axis_scores: Record<string, number>;
  comment: string | null;
  is_public_consented: boolean;
  is_curated: boolean;
  curated_at: string | null;
  created_at: string | null;
}

export interface ReviewCandidate {
  review: InterpretationReview;
  snapshot: InterpretationSnapshot | null;
}

export interface AxisBreakdownRow {
  axis: string;
  n: number;
  mean: number;
  user_overall_mean: number;
  /** Gap between this axis and the user's own overall mean. This is the signal. */
  deviation: number;
}

export interface UserReviewProfile {
  user_id: string;
  /** How well the user feels readings land (user -> us). */
  satisfaction_score: number | null;
  /** How much their answers inform us (us -> user). Deliberately a separate number. */
  reviewer_weight: number;
  axis_breakdown: AxisBreakdownRow[];
}

export const AXIS_LABELS: Record<string, string> = {
  jaemul: '재물운',
  aejeong: '애정운',
  jikup: '직업운',
  geongang: '건강운',
  janyeo: '자녀운',
  idong: '이동운',
};

export const TARGET_TYPE_LABELS: Record<string, string> = {
  iljin: '일진',
  wolun: '월운',
  seun: '세운',
  daeun: '대운',
  palja: '사주팔자',
};

export function axisLabel(axis: string): string {
  return AXIS_LABELS[axis] ?? axis;
}

export function targetTypeLabel(targetType: string): string {
  return TARGET_TYPE_LABELS[targetType] ?? targetType;
}

/**
 * A reviewer whose answers never vary separates no axes, so their reviews cannot tell us which
 * axis fits them. Surfaced so curation does not mistake volume for signal.
 */
export function isLowInformationReviewer(profile: UserReviewProfile): boolean {
  return profile.reviewer_weight < 0.3;
}

/**
 * The axes that stand out for this user, strongest agreement first.
 *
 * Filters by deviation rather than mean on purpose: a lenient reviewer's 4.5 and a strict
 * reviewer's 3.0 can both be their top axis, and the raw numbers hide that.
 */
export function notableAxes(rows: AxisBreakdownRow[], threshold = 0.5): AxisBreakdownRow[] {
  return rows
    .filter((row) => Math.abs(row.deviation) >= threshold)
    .sort((a, b) => b.deviation - a.deviation);
}
